import React from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { capitalize, isFunction, isString, map, memoize } from "lodash";

import { isMuted } from "selectors/chat/chat";
import { muteChat, unmuteChat, muteAppChat, unmuteAppChat } from "actions/chat";
import { faVolume, faVolumeSlash } from "@fortawesome/pro-solid-svg-icons";
import DropDown from "components/shared/DropDown";
import {
  faFiles,
  faImages,
  faLink,
  faPaperclip,
} from "@fortawesome/pro-regular-svg-icons";

const MUTE_DURATIONS = memoize(() => [
  [
    I18n.t("js.chat.room.mute_hour"),
    () => moment().add(1, "hour").toDate(),
    false,
  ],
  [
    I18n.t("js.chat.room.mute_rest_of_day"),
    () => moment().endOf("day").toDate(),
    false,
  ],
  [
    I18n.t("js.chat.room.mute_rest_of_week"),
    () => moment().endOf("week").toDate(),
    false,
  ],
  [I18n.t("js.chat.room.forever"), () => moment("9999-12-31").toDate(), true],
]);

type ChatRoomSettingsButtonArgs = {
  chatId: string;
};
export default function ChatRoomSettingsButton({
  chatId,
}: ChatRoomSettingsButtonArgs) {
  const dispatch = useDispatch();
  const muted = useSelector((state) => isMuted(state, { chatId }));

  function onMute(
    e: React.MouseEvent<HTMLAnchorElement>,
    muteUntil?: () => Date,
  ) {
    e.preventDefault();
    const meta = { chatId };
    let dispatchAction;

    if (muteUntil) {
      dispatchAction = window.isApp ? muteAppChat : muteChat;
    } else {
      dispatchAction = window.isApp ? unmuteAppChat : unmuteChat;
    }

    dispatch(
      dispatchAction({
        meta,
        payload: muteUntil ? { muted_until: muteUntil() } : {},
      }),
    );
  }
  function showAttachments(e: React.MouseEvent<HTMLAnchorElement>, route) {
    e.preventDefault();
    dispatch({
      type: "chat/NAVIGATE",
      payload: { route, params: { chatId } },
    });
  }

  const attachmentTypes = [
    { type: "files", icon: faFiles },
    { type: "images", icon: faImages },
    { type: "links", icon: faLink },
  ];

  return (
    <div className="chatRoomSettingsButton chatHeaderActions">
      <div className="flex">
        <DropDown.Toggle
          small
          className="chat__header_action"
          title={I18n.t("js.chat.room.mute.label")}
          aria-label={I18n.t("js.chat.room.mute.label")}
          buttonType="normal"
          icon={muted ? faVolumeSlash : faVolume}
        />
        <DropDown className="chat__header_dropdown">
          {muted ? (
            <DropDown.Item
              text={I18n.t("js.chat.room.mute_disable")}
              onSelect={(e) => onMute(e)}
            />
          ) : (
            MUTE_DURATIONS().map(([label, muteUntil, appOnly], i) =>
              appOnly && !window.isApp ? null : (
                <DropDown.Item
                  key={i}
                  text={isString(label) ? label : ""}
                  onSelect={(e) =>
                    isFunction(muteUntil) && onMute(e, muteUntil)
                  }
                />
              ),
            )
          )}
        </DropDown>
        <DropDown.Toggle
          className="chat__header_action"
          title={I18n.t("js.chat.room.attachments.title")}
          aria-label={I18n.t("js.chat.room.attachments.title")}
          icon={faPaperclip}
          buttonType="normal"
          small
        />
        <DropDown>
          {map(attachmentTypes, ({ type, icon }) => (
            <DropDown.Item
              key={type}
              text={I18n.t(`js.chat.room.show_${type}`)}
              icon={icon}
              onSelect={(e) =>
                showAttachments(e, `showChat${capitalize(type)}`)
              }
            />
          ))}
        </DropDown>
      </div>
    </div>
  );
}
